import moment from "moment";
import momentTZ from "moment-timezone";
import type { IntlShape } from "react-intl";

type RawStop = Record<string, unknown>;

export type NormalizedScheduleStop = {
  stopType: string;
  isPickup: boolean;
  addressName?: string;
  streetLine?: string;
  regionLine?: string;
  portLabel?: string;
  addressType?: string;
  isPortAddress: boolean;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  contactSummary?: string;
  hoursStart?: string;
  hoursEnd?: string;
  requestedTime: string;
  instructions?: string;
  unLocode?: string;
  locationReferenceNumber?: string;
  customAddressReference?: string;
};

const isPickupStop = (stopType: unknown) => {
  const normalized = String(stopType ?? "").toLowerCase();
  return normalized === "pickup" || normalized === "pick up";
};

const formatHours = (time?: string) => {
  if (!time) return "";
  return moment(time, ["HH:mm:ss", "HH:mm", "hh:mm:ss"]).format("HH:mm");
};

const formatMultistopDatetime = (datetime?: string, timezone?: string) => {
  if (!datetime || !timezone) return null;
  return momentTZ(datetime).tz(timezone).format("DD MMM YYYY, HH:mm (UTC Z)");
};

const fieldText = (value: unknown): string =>
  typeof value === "string" || typeof value === "number" ? String(value).trim() : "";

const stopAirportCode = (stop: RawStop): string => {
  const details = stop.airport_details as Record<string, unknown> | undefined;
  return fieldText(details?.code) || fieldText(stop.stop_airport_code) || fieldText(stop.airport_code);
};

const stopSeaportCode = (stop: RawStop): string => {
  const details = stop.seaport_details as Record<string, unknown> | undefined;
  return fieldText(details?.un_locode) || fieldText(stop.stop_seaport_code) || fieldText(stop.seaport_code);
};

export const getScheduleStopLocationKey = (stop: unknown): string => {
  const raw = (stop ?? {}) as RawStop;
  const addressType = String(raw.stop_address_type ?? raw.address_type ?? "").toLowerCase();
  if (addressType.includes("airport")) {
    const code = stopAirportCode(raw);
    return code ? `airport:${code}` : "";
  }
  if (addressType.includes("port")) {
    const code = stopSeaportCode(raw);
    return code ? `seaport:${code}` : "";
  }
  const reference = fieldText(raw.address_reference_number);
  return reference ? `address:${reference}` : "";
};

const formatPortCodeAndPlace = (
  code?: string,
  name?: string,
  city?: string,
  country?: string,
): string | undefined => {
  const trimmedCode = (code ?? "").trim();
  const place = (city || name || "").trim();
  const trimmedCountry = (country ?? "").trim();
  const withCountry =
    trimmedCountry && place && !place.toLowerCase().includes(trimmedCountry.toLowerCase())
      ? `${place}, ${trimmedCountry}`
      : place;

  if (!trimmedCode && !withCountry) return undefined;
  if (!trimmedCode) return withCountry;
  if (!withCountry) return trimmedCode;
  return `${trimmedCode} - ${withCountry}`;
};

const getPortLabel = (stop: RawStop): string | undefined => {
  const addressType = String(stop.stop_address_type ?? stop.address_type ?? "");
  if (addressType === "Airport") {
    const airportDetails = stop.airport_details as Record<string, unknown> | undefined;
    return formatPortCodeAndPlace(
      stopAirportCode(stop),
      (airportDetails?.name as string) || (stop.stop_airport_name as string) || (stop.airport as string),
      (airportDetails?.city_name as string) || (stop.stop_airport_city as string),
      (airportDetails?.country_name as string) || (stop.stop_airport_country as string),
    );
  }
  if (addressType === "Port") {
    const seaportDetails = stop.seaport_details as Record<string, unknown> | undefined;
    return formatPortCodeAndPlace(
      stopSeaportCode(stop),
      (seaportDetails?.name as string) || (stop.stop_seaport_name as string) || (stop.seaport as string),
      (seaportDetails?.city_name as string) || (stop.stop_seaport_city as string),
      (seaportDetails?.country_name as string) || (stop.stop_seaport_country as string),
    );
  }
  return undefined;
};

const resolveTimeKind = (
  rawType: unknown,
  isPickup: boolean,
): "after" | "before" | "between" | "asap" | undefined => {
  if (rawType === undefined || rawType === null || rawType === "") return undefined;
  const asString = String(rawType).toLowerCase();
  if (asString === "asap" || asString === "asap delivery" || rawType === 3) return "asap";
  if (asString === "between" || rawType === 2) return "between";
  if (asString === "before") return "before";
  if (asString === "after") return "after";
  if (rawType === 1) return isPickup ? "after" : "before";
  return undefined;
};

const formatSyntheticRequestedTime = (stop: RawStop, intl: IntlShape, isPickup: boolean): string => {
  const datetime = stop.datetime as string | undefined;
  const endDatetime = stop.end_datetime as string | undefined;
  const kind = resolveTimeKind(stop.time_type ?? stop.stop_time_type, isPickup);

  if (kind === "asap" || endDatetime === "ASAP") {
    return intl.formatMessage({ id: "quote-request-detail.asap", defaultMessage: "ASAP" });
  }

  if (!datetime) return "-";

  if (kind === "between" || endDatetime) {
    if (endDatetime && endDatetime !== "ASAP") {
      return `${datetime} - ${endDatetime}`;
    }
  }

  if (kind === "before") {
    return `${intl.formatMessage({ id: "general.before", defaultMessage: "Before" })} ${datetime}`;
  }

  if (kind === "after") {
    return `${intl.formatMessage({ id: "general.after", defaultMessage: "After" })} ${datetime}`;
  }

  return datetime;
};

const formatMultistopRequestedTime = (stop: RawStop, intl: IntlShape): string => {
  const stopTimeType = stop.stop_time_type as string | undefined;
  const timezone = stop.stop_timezone as string | undefined;
  const afterDateTime = formatMultistopDatetime(stop.stop_after_datetime as string | undefined, timezone);
  const beforeDateTime = formatMultistopDatetime(stop.stop_before_datetime as string | undefined, timezone);
  const capitalize = (text = "") => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "");

  if (stopTimeType === "After") {
    if (!afterDateTime) return "-";
    return `${capitalize(intl.formatMessage({ id: "general.after", defaultMessage: "After" }))} ${afterDateTime}`;
  }
  if (stopTimeType === "Before") {
    if (!beforeDateTime) return "-";
    return `${capitalize(intl.formatMessage({ id: "general.before", defaultMessage: "Before" }))} ${beforeDateTime}`;
  }
  if (stopTimeType === "Between") {
    if (!afterDateTime || !beforeDateTime) return "-";
    return `${afterDateTime} - ${beforeDateTime}`;
  }
  if (stopTimeType === "ASAP" || stopTimeType === "ASAP Delivery") {
    return intl.formatMessage({ id: "quote-request-detail.asap", defaultMessage: "ASAP" });
  }

  return afterDateTime || beforeDateTime || "-";
};

export const normalizeScheduleStop = (stop: unknown, intl: IntlShape): NormalizedScheduleStop => {
  const raw = (stop ?? {}) as RawStop;
  const addressDetails = raw.address_details as Record<string, unknown> | undefined;
  const isMultistopShape = !!(
    addressDetails ||
    raw.stop_after_datetime ||
    raw.stop_before_datetime ||
    raw.stop_time_type
  );

  const stopType = String(raw.stop_type ?? "");
  const isPickup = isPickupStop(stopType);

  const addressName = (addressDetails?.name as string) || (raw.address_name as string) || undefined;
  const street = (addressDetails?.street_address as string) || (raw.street as string) || undefined;
  const streetLineTwo =
    (addressDetails?.street_address_line_two as string) || (raw.street_line_two as string) || undefined;
  const streetLine = [street, streetLineTwo].filter(Boolean).join(", ") || undefined;

  const city = (addressDetails?.city as string) || (raw.city as string) || undefined;
  const state = (addressDetails?.state as string) || (raw.state as string) || undefined;
  const country = (addressDetails?.country as string) || (raw.country as string) || undefined;
  const zipcode = (addressDetails?.zipcode as string) || (raw.zipcode as string) || undefined;
  const regionLine = [city, state, country, zipcode].filter(Boolean).join(", ") || undefined;

  const contactName = (raw.stop_contact_name as string) || (raw.contact_name as string) || undefined;
  const contactPhone = (raw.stop_contact_number as string) || (raw.contact_phone as string) || undefined;
  const contactEmail = (raw.stop_contact_email as string) || (raw.contact_email as string) || undefined;
  const contactSummary = [contactName, contactPhone, contactEmail].filter(Boolean).join(" | ") || undefined;

  const hoursStart = (raw.stop_hours_start as string) || (raw.hours_start as string) || undefined;
  const hoursEnd = (raw.stop_hours_end as string) || (raw.hours_end as string) || undefined;

  const instructions = (raw.stop_notes as string) || (raw.notes as string) || undefined;

  const requestedTime = isMultistopShape
    ? formatMultistopRequestedTime(raw, intl)
    : formatSyntheticRequestedTime(raw, intl, isPickup);

  return {
    stopType,
    isPickup,
    addressName,
    streetLine,
    regionLine,
    portLabel: getPortLabel(raw),
    addressType: String(raw.stop_address_type ?? raw.address_type ?? ""),
    isPortAddress: Boolean(raw.is_port_address),
    contactName,
    contactPhone,
    contactEmail,
    contactSummary,
    hoursStart,
    hoursEnd,
    requestedTime,
    instructions,
    unLocode: (addressDetails?.un_locode as string) || (raw.un_locode as string) || undefined,
    locationReferenceNumber:
      (addressDetails?.location_reference_number as string) || (raw.location_reference_number as string) || undefined,
    customAddressReference:
      (addressDetails?.custom_address_reference as string) || (raw.custom_address_reference as string) || undefined,
  };
};

export const formatScheduleHours = (hoursStart?: string, hoursEnd?: string) => {
  if (!hoursStart || !hoursEnd) return null;
  return `${formatHours(hoursStart)} - ${formatHours(hoursEnd)}`;
};
